import { useAuth } from '../hooks/useAuth'
import { usePreferences } from '../hooks/usePreferences'

const COMMON_TIMEZONES = [
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Zurich',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Helsinki',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Australia/Perth',
  'Pacific/Auckland',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'America/Sao_Paulo',
  'Africa/Johannesburg',
  'Africa/Cairo',
  'Africa/Lagos',
]

function tzLabel(tz: string) {
  try {
    const offset = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(new Date())
      .find(p => p.type === 'timeZoneName')?.value ?? ''
    return `${tz.replace(/_/g, ' ').replace(/\//g, ' / ')} (${offset})`
  } catch {
    return tz
  }
}

export function SettingsPage() {
  const { profile } = useAuth()
  const { prefs, update, formatDate, formatTime } = usePreferences()
  const now = new Date()

  const selectCls = 'w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
        <p className="text-gray-500 text-xs mt-0.5">Manage your profile and display preferences</p>
      </div>

      <div className="grid gap-6 max-w-2xl">
        {/* Profile info */}
        <div className="bg-card border border-card-border rounded-lg p-5">
          <h2 className="text-xs uppercase tracking-wide text-brand-dark font-semibold mb-4">Profile</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Email</label>
              <p className="text-sm text-gray-900 mt-0.5">{profile?.email}</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Role</label>
              <p className="text-sm text-gray-900 mt-0.5 capitalize">{profile?.role.replace('_', ' ')}</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Organization</label>
              <p className="text-sm text-gray-900 mt-0.5">{profile?.org_name ?? 'None'}</p>
            </div>
          </div>
        </div>

        {/* Date & Time Preferences */}
        <div className="bg-card border border-card-border rounded-lg p-5">
          <h2 className="text-xs uppercase tracking-wide text-brand-dark font-semibold mb-4">Date & Time</h2>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-gray-500 font-medium block mb-1">Date Format</label>
              <select
                value={prefs.dateFormat}
                onChange={e => update({ dateFormat: e.target.value as typeof prefs.dateFormat })}
                className={selectCls}
              >
                <option value="dd/MM/yyyy">DD/MM/YYYY (31/12/2026)</option>
                <option value="MM/dd/yyyy">MM/DD/YYYY (12/31/2026)</option>
                <option value="yyyy-MM-dd">YYYY-MM-DD (2026-12-31)</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wide text-gray-500 font-medium block mb-1">Time Format</label>
              <select
                value={prefs.timeFormat}
                onChange={e => update({ timeFormat: e.target.value as '12h' | '24h' })}
                className={selectCls}
              >
                <option value="24h">24-hour (14:30:00)</option>
                <option value="12h">12-hour (2:30:00 PM)</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wide text-gray-500 font-medium block mb-1">Timezone</label>
              <select
                value={prefs.timezone}
                onChange={e => update({ timezone: e.target.value })}
                className={selectCls}
              >
                {(!COMMON_TIMEZONES.includes(prefs.timezone)) && (
                  <option value={prefs.timezone}>{tzLabel(prefs.timezone)}</option>
                )}
                {COMMON_TIMEZONES.map(tz => (
                  <option key={tz} value={tz}>{tzLabel(tz)}</option>
                ))}
              </select>
            </div>

            {/* Live preview */}
            <div className="bg-gray-50 rounded-md px-4 py-3 border border-gray-100">
              <p className="text-[10px] uppercase tracking-wide text-gray-500 font-medium mb-2">Preview</p>
              <div className="flex gap-6">
                <div>
                  <p className="text-[10px] text-gray-400">Date</p>
                  <p className="text-sm font-mono text-gray-900">{formatDate(now)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">Time</p>
                  <p className="text-sm font-mono text-gray-900">{formatTime(now)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
